/**
 * Runtime validators for every request payload of the protocol. The map is typed against
 * `RequestMap` (messages.ts), so a missing validator or a validator whose accepted shape drifts from the
 * declared payload type is a compile error.
 */
import { MAX_TICK, validateCommandLogData, validateInputCommand } from '../../session/command-log';
import type { CommandLogData } from '../../session/command-log';
import { type InputCommand, KEY_OTHER_VALUES } from '../../session/input';
import { ONLINE_MAPS, ONLINE_MAP_IDS, onlineMapOf } from '../../session/online';
import { type JsonObject, checkJsonData, isPlainObject, utf8Length } from '../json';
import { REQUEST_ID_PATTERN } from './envelope';
import { type PayloadOf, type RequestType } from './messages';
import { AD_PLACEMENT_LIST, AD_PROVIDERS, BATTLE_MODES, CLAN_JOIN_POLICIES, INTEGRITY_PROVIDERS, LEADERBOARD_BOARDS, MESSAGE_LANGUAGES, PROFILE_SUMMARY_KEYS, type RoomSettingsInput, SAVE_REASONS } from './model';
import { type Issue, type JsonSchema, type Validator, formatIssues, s, validate } from './schema';
import { NICKNAME_INPUT_MAX, PASSWORD_INPUT_MAX } from '../auth-rules';
import { EMAIL_INPUT_MAX } from '../email-rules';
import { CLAN_DESCRIPTION_INPUT_MAX, CLAN_NAME_INPUT_MAX, CLAN_QUERY_MAX, CLAN_QUERY_MIN } from '../clan-rules';
import { CHAT_CHANNEL_PATTERN, CHAT_TEXT_INPUT_MAX } from '../chat-rules';

/** Protocol-level size and range limits shared by client and server. */
export const LIMITS = {
  maxRequestBytes: 1024 * 1024,
  maxGameStateBytes: 256 * 1024,
  maxGameStateDepth: 32,
  maxBattleParamsBytes: 64 * 1024,
  displayNameMax: 24,
  usernameMin: 3,
  usernameMax: 32,
  passwordMin: 8,
  passwordMax: 128,
  chatMax: 200,
  maxInputBatch: 1000,
  maxPublicBatch: 100,
  maxConsumedGrants: 100,
  leaderboardPageSize: 10,
  leaderboardMaxPageSize: 50,
  leaderboardMaxPages: 10,
  maxLegacyReplayChars: 200_000,
  /** 777,599,999 = largest 5-digit base-60 value of the legacy save format (vk-and-network.md §4.2). */
  maxCounter: 777_599_999,
  /** player.command arguments (a battle's command log is the largest). */
  maxCommandArgsBytes: 256 * 1024,
  maxCommandArgsDepth: 16,
  /**
   * Device time zone offsets (minutes east of UTC) in use: UTC-12:00 .. UTC+14:00, the range the shared rules accept
   * (src/game/rules/context.ts MIN_TZ_OFFSET_MIN / MAX_TZ_OFFSET_MIN).
   */
  tzOffsetMinMin: -12 * 60,
  tzOffsetMinMax: 14 * 60,
} as const;

export const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
export const TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,256}$/;
export const USERNAME_PATTERN = /^[A-Za-z0-9_.-]+$/;
export const DEF_KEY_PATTERN = /^[A-Za-z0-9_]{1,48}$/;
/** player.command op ids: rule op names like 'shop.buy' or 'arsenal.sellWeapon'. */
export const COMMAND_OP_PATTERN = /^[A-Za-z.]{1,64}$/;
/** Opaque list cursors (`next` of a page, passed back as `cursor`). */
export const CURSOR_PATTERN = /^[A-Za-z0-9_.-]{1,128}$/;
/** system.hello capabilities: identifiers such as 'events.resume'. */
export const CAPABILITY_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;

/**
 * `limit` of the paginated list requests: the default when absent and the largest accepted value (system.hello
 * reports them in limits.pageSizes; the leaderboard's default page size is the server's LeaderboardConfig).
 */
export const LIST_LIMITS = {
  'leaderboard.get': { default: LIMITS.leaderboardPageSize, max: LIMITS.leaderboardMaxPageSize },
  'clan.members': { default: 100, max: 100 },
  'news.list': { default: 20, max: 100 },
  'replay.list': { default: 20, max: 100 },
  'room.list': { default: 20, max: 100 },
  'profile.listGrants': { default: 100, max: 100 },
  'clan.list': { default: 20, max: 100 },
  'clan.requests': { default: 50, max: 50 },
  'clan.history': { default: 20, max: 100 },
  'chat.history': { default: 50, max: 100 },
} as const;

/** Clan ids: positive 32-bit integers (the first player-made clan is 101). */
export const CLAN_ID_MAX = 2_147_483_647;

/** DeviceInfo.platform: a lower-case identifier such as 'web', 'android', 'ios'. */
export const DEVICE_PLATFORM_PATTERN = /^[a-z][a-z0-9_-]{0,15}$/;
/** Bounds of the DeviceInfo strings (auth requests). */
export const DEVICE_LIMITS = { modelMax: 64, appVersionMax: 32, labelMax: 64 } as const;

const id = s.string({ pattern: ID_PATTERN });
const token = s.string({ pattern: TOKEN_PATTERN });
const displayName = s.string({ min: 1, max: LIMITS.displayNameMax, trimmed: true });
const username = s.string({ min: LIMITS.usernameMin, max: LIMITS.usernameMax, pattern: USERNAME_PATTERN });
const password = s.string({ min: LIMITS.passwordMin, max: LIMITS.passwordMax });
/**
 * Nickname and password of auth.signUp / auth.signIn: any string up to a generous bound (control characters and lone
 * surrogates included), so that the service answers with the specific reason of src/shared/auth-rules.ts
 * (VALIDATION_FAILED 'nickname_invalid', 'password_too_long'...); a non-string or a longer one is a malformed payload.
 */
const nicknameInput = s.string({ max: NICKNAME_INPUT_MAX, noControl: false, wellFormed: false });
const passwordInput = s.string({ max: PASSWORD_INPUT_MAX, noControl: false, wellFormed: false });
const counter = s.int({ min: 0, max: LIMITS.maxCounter });
const empty = s.object({});
/**
 * A Google Play Games server auth code (GamesSignInClient.requestServerSideAccess): an OAuth authorization code,
 * printable ASCII without spaces.
 */
const serverAuthCode = s.string({ min: 8, max: 1024, pattern: /^[\x21-\x7e]+$/ });
/**
 * An e-mail address as typed: any string up to a generous bound, so that the service answers with the specific reason
 * of src/shared/email-rules.ts (VALIDATION_FAILED 'email_invalid', 'email_too_long'...).
 */
const emailInput = s.string({ max: EMAIL_INPUT_MAX, noControl: false, wellFormed: false });
/** The language of the server's messages (e-mails, notifications). */
const messageLanguage = s.optional(s.literal(...MESSAGE_LANGUAGES));
/** A Play Integrity token (a JWE in compact form) and an FCM registration token: printable ASCII without spaces. */
const integrityToken = s.string({ min: 16, max: 8192, pattern: /^[\x21-\x7e]+$/ });
const pushToken = s.string({ min: 16, max: 4096, pattern: /^[\x21-\x7e]+$/ });
/** An ad ticket id (ads.start). */
const adTicketId = s.string({ min: 3, max: 64, pattern: /^[A-Za-z0-9_-]+$/ });
const clanId = s.int({ min: 1, max: CLAN_ID_MAX });
/**
 * Clan name and description as typed: any string up to a generous bound (control characters and lone surrogates
 * included), so that the service answers with the specific reason of src/shared/clan-rules.ts (VALIDATION_FAILED
 * 'clan_name_too_long', 'description_invalid'...).
 */
const clanName = s.string({ max: CLAN_NAME_INPUT_MAX, noControl: false, wellFormed: false });
const clanDescription = s.string({ max: CLAN_DESCRIPTION_INPUT_MAX, noControl: false, wellFormed: false });
/** A chat channel: 'global' or 'clan:<clanId>' (src/shared/chat-rules.ts). */
const chatChannel = s.string({ pattern: CHAT_CHANNEL_PATTERN });
/**
 * A chat message as typed: any string up to a generous bound (control characters and lone surrogates included): the
 * service sanitizes it (src/shared/chat-rules.ts) and answers an empty or too long result with its specific reason.
 */
const chatText = s.string({ max: CHAT_TEXT_INPUT_MAX, noControl: false, wellFormed: false });
/** DeviceInfo of the auth requests: strict (no other fields), bounded, no control characters. */
export const deviceInfoValidator = s.object({
  platform: s.string({ pattern: DEVICE_PLATFORM_PATTERN }),
  model: s.optional(s.string({ min: 1, max: DEVICE_LIMITS.modelMax, trimmed: true })),
  appVersion: s.optional(s.string({ min: 1, max: DEVICE_LIMITS.appVersionMax, trimmed: true })),
  label: s.optional(s.string({ min: 1, max: DEVICE_LIMITS.labelMax, trimmed: true })),
});
const device = s.optional(deviceInfoValidator);

const summaryShape = Object.fromEntries(PROFILE_SUMMARY_KEYS.map((k) => [k, s.optional(counter)])) as {
  [K in (typeof PROFILE_SUMMARY_KEYS)[number]]: ReturnType<typeof s.optional<number>>;
};
export const summaryInputValidator = s.object(summaryShape);

export const teamSnapshotInputValidator = s.object({
  level: s.optional(s.int({ min: 0, max: 10_000 })),
  attack: s.optional(counter),
  protect: s.optional(counter),
  health: s.optional(counter),
  worms: s.optional(s.int({ min: 0, max: 16 })),
  spec: s.optional(s.int({ min: 0, max: 16 })),
  perks: s.optional(s.record(s.int({ min: 0, max: 1000 }), { maxKeys: 64, keyPattern: DEF_KEY_PATTERN })),
  weapons: s.optional(s.record(s.int({ min: 0, max: 10_000 }), { maxKeys: 400, keyPattern: DEF_KEY_PATTERN })),
  wormNames: s.optional(s.array(s.string({ max: 15 }), { max: 8 })),
  implant: s.optional(s.int({ min: 0, max: 10_000 })),
  flag: s.optional(s.int({ min: 0, max: 10_000 })),
});

/**
 * RoomSettings of a request: the map is one of the PvP maps (ONLINE_MAPS), named by its stable `mapId` or by its
 * number `mapIndex` (the field of the older clients), or by both when they name the same map; 2..4 teams.
 */
export const roomSettingsValidator: Validator<RoomSettingsInput> = s.refine(
  s.object({
    mapId: s.optional(s.literal(...ONLINE_MAP_IDS)),
    mapIndex: s.optional(s.int({ min: 1, max: ONLINE_MAPS.length })),
    teams: s.array(
      s.object({
        worms: s.int({ min: 1, max: 8 }),
        color: s.int({ min: 1, max: 7 }),
        name: s.string({ min: 1, max: 20, trimmed: true }),
      }),
      { min: 2, max: 4 },
    ),
  }),
  (v) => {
    if (v.mapId === undefined && v.mapIndex === undefined) return 'names no map: mapId (or mapIndex) is required';
    if (!onlineMapOf(v)) return `mapIndex ${v.mapIndex} is not the number of map ${v.mapId}`;
    return v.teams.reduce((n, t) => n + t.worms, 0) > 12 ? 'at most 12 worms in total (new_interf.as:3346)' : null;
  },
  { description: 'the map by mapId or mapIndex (both: the same map); at most 12 worms in total', schema: { anyOf: [{ required: ['mapId'] }, { required: ['mapIndex'] }] } },
);

export const battleInitValidator = s.object({
  mode: s.literal(...BATTLE_MODES),
  stage: s.int({ min: 0, max: 100_000 }),
  mapId: s.optional(s.string({ min: 1, max: 64, pattern: /^[A-Za-z0-9_.-]+$/ })),
  zone: s.optional(s.int({ min: 1, max: 64 })),
  zoneStage: s.optional(s.int({ min: 0, max: LIMITS.maxCounter })),
  skill: s.optional(s.int({ min: 0, max: 20 })),
  teams: s.optional(roomSettingsValidator),
  params: s.optional(s.json({ maxBytes: LIMITS.maxBattleParamsBytes, maxDepth: 16 })),
});

const HELD_KEYS = KEY_OTHER_VALUES.filter((k) => k !== 'none' && k !== 'weapon' && k !== 'click');
const frameNumber: JsonSchema = { type: 'number', minimum: -1e7, maximum: 1e7 };
const tickSchema: JsonSchema = { type: 'integer', minimum: 0, maximum: MAX_TICK };

/** JSON Schema of an InputCommand (src/session/command-log.ts validateInputCommand). */
export const INPUT_COMMAND_SCHEMA: JsonSchema = {
  type: 'object',
  description: 'Input held from `tick` for `repeat` more ticks; one-shot frames (weapon, click, scanner, surrender) have repeat 0',
  properties: {
    tick: tickSchema,
    repeat: tickSchema,
    frame: {
      type: 'object',
      description: "One tick of input; 'weapon'/'click' frames have space false, a surrender carries no keys",
      properties: {
        space: { type: 'boolean' },
        other: { enum: [...KEY_OTHER_VALUES] },
        weapon: frameNumber,
        range: frameNumber,
        clickX: frameNumber,
        clickY: frameNumber,
        key: { enum: HELD_KEYS },
        scanner: { type: 'integer', minimum: 1, maximum: 100 },
        surrender: { const: true },
      },
      required: ['space', 'other'],
      additionalProperties: false,
    },
  },
  required: ['tick', 'repeat', 'frame'],
  additionalProperties: false,
};

/** JSON Schema of a CommandLogData (validateCommandLogData also checks the turn/tick order). */
export const COMMAND_LOG_SCHEMA: JsonSchema = {
  type: 'object',
  description: 'Battle input by turns (global ticks); turns are consecutive, commands ordered and inside their turn',
  properties: {
    format: { const: 'fwp-command-log' },
    version: { const: 1 },
    turns: {
      type: 'array',
      minItems: 1,
      maxItems: 100_000,
      items: {
        type: 'object',
        properties: {
          turn: { type: 'integer', minimum: 0 },
          team: { type: 'integer', minimum: 1, maximum: 64 },
          startTick: tickSchema,
          endTick: { anyOf: [tickSchema, { type: 'null' }] },
          throughTick: { type: 'integer', minimum: -1 },
          commands: { type: 'array', items: INPUT_COMMAND_SCHEMA },
          forcedAt: { type: 'integer', minimum: -1, description: 'forced turn: the input was closed after this tick, later ticks are empty' },
        },
        required: ['turn', 'team', 'startTick', 'endTick', 'throughTick', 'commands'],
        additionalProperties: false,
      },
    },
  },
  required: ['format', 'version', 'turns'],
  additionalProperties: false,
};

export const inputCommandValidator: Validator<InputCommand> = s.custom('inputCommand', (v) => validateInputCommand(v, 'command'), INPUT_COMMAND_SCHEMA);
export const commandLogValidator: Validator<CommandLogData> = s.custom('commandLog', (v) => validateCommandLogData(v), COMMAND_LOG_SCHEMA);
/**
 * The log of replay.save (the client's own recording, stored as it came): bounded like the log of battle.finish (its
 * player.command args, LIMITS.maxCommandArgsBytes), so one account's replays cannot fill the server's memory and disk
 * a megabyte at a time. Same published schema as every command log (the byte bound is listed in the API limits).
 */
const replayLogValidator: Validator<CommandLogData> = s.custom(
  'commandLog',
  (v) => {
    const err = validateCommandLogData(v);
    if (err) return err;
    const size = utf8Length(JSON.stringify(v));
    return size > LIMITS.maxCommandArgsBytes ? `is ${size} bytes, limit is ${LIMITS.maxCommandArgsBytes}` : null;
  },
  COMMAND_LOG_SCHEMA,
);
/** An opaque list cursor. */
export const cursorValidator = s.string({ min: 1, max: 128, pattern: CURSOR_PATTERN });

/**
 * player.command arguments: a plain JSON object, bounded in size and depth, its strings and member names well-formed
 * (checkJsonData: no lone UTF-16 surrogate); the rule op checks the fields.
 */
export const commandArgsValidator: Validator<JsonObject> = s.custom('commandArgs', (v) => {
  if (!isPlainObject(v)) return `expected object, got ${v === null ? 'null' : Array.isArray(v) ? 'array' : typeof v}`;
  const err = checkJsonData(v, LIMITS.maxCommandArgsDepth, 'args');
  if (err) return err;
  const size = utf8Length(JSON.stringify(v));
  return size > LIMITS.maxCommandArgsBytes ? `is ${size} bytes, limit is ${LIMITS.maxCommandArgsBytes}` : null;
}, {
  type: 'object',
  description: `the op's arguments (any JSON object of at most ${LIMITS.maxCommandArgsBytes} bytes, depth ${LIMITS.maxCommandArgsDepth}, no lone UTF-16 surrogate in its strings and member names)`,
});

const battleResult = s.object({ winnerTeam: s.int({ min: 0, max: 64 }) });
const hash = s.string({ min: 1, max: 128, pattern: /^[A-Za-z0-9:_-]+$/ });

export const PAYLOAD_VALIDATORS: { readonly [K in RequestType]: Validator<PayloadOf<K>> } = {
  'system.hello': s.object({
    clientVersion: s.string({ min: 1, max: 64 }),
    protocols: s.array(s.int({ min: 1, max: 1000 }), { min: 1, max: 16 }),
    platform: s.optional(s.string({ pattern: DEVICE_PLATFORM_PATTERN })),
    capabilities: s.optional(s.array(s.string({ pattern: CAPABILITY_PATTERN }), { max: 32 })),
  }),
  'system.time': empty,
  'system.describe': empty,
  'events.poll': s.object({ after: s.optional(s.int({ min: 0, max: Number.MAX_SAFE_INTEGER })), waitMs: s.optional(s.int({ min: 0, max: 30_000 })) }),
  'events.resume': s.object({ after: s.optional(s.int({ min: 0, max: Number.MAX_SAFE_INTEGER })) }),

  'auth.guest': s.object({ deviceToken: s.optional(token), displayName: s.optional(displayName), device }),
  'auth.signUp': s.object({ nickname: nicknameInput, password: passwordInput, device }),
  'auth.signIn': s.object({ nickname: nicknameInput, password: passwordInput, device }),
  'auth.session': empty,
  'auth.register': s.object({ username, password, displayName: s.optional(displayName), device }),
  'auth.login': s.object({ username, password, device }),
  'auth.resume': s.object({ token, device }),
  'auth.link': s.object({ username, password, device }),
  'auth.refresh': s.object({ refreshToken: token, device }),
  'auth.sessions': empty,
  'auth.revoke': s.object({ sessionId: id }),
  'auth.logout': s.object({ all: s.optional(s.boolean()) }),
  'auth.playGames': s.object({ serverAuthCode, device }),
  'auth.linkPlayGames': s.object({ serverAuthCode }),
  'auth.unlinkPlayGames': empty,
  'auth.playGamesStatus': empty,
  'auth.emailSignUp': s.object({ nickname: nicknameInput, email: emailInput, password: passwordInput, lang: messageLanguage, device }),
  'auth.emailSignIn': s.object({ email: emailInput, password: passwordInput, device }),
  'auth.emailStatus': empty,
  'auth.emailChange': s.object({ email: emailInput, password: passwordInput, lang: messageLanguage }),
  'auth.emailResend': s.object({ lang: messageLanguage }),
  'auth.emailVerify': s.object({ token }),
  'auth.passwordResetRequest': s.object({ email: emailInput, lang: messageLanguage }),
  'auth.passwordReset': s.object({ token, password: passwordInput }),
  'integrity.challenge': empty,
  'integrity.verify': s.object({ token: integrityToken, challenge: token, provider: s.optional(s.literal(...INTEGRITY_PROVIDERS)) }),
  'push.register': s.object({ token: pushToken, platform: s.literal('android', 'ios', 'web'), lang: messageLanguage }),
  'push.unregister': s.object({ token: s.optional(pushToken) }),
  'push.test': empty,
  // The password of an account that has one (checked by the service with the sign-in's own rules and limits).
  'account.delete': s.object({ password: s.optional(passwordInput) }),

  'profile.load': s.object({ tzOffsetMin: s.optional(s.int({ min: LIMITS.tzOffsetMinMin, max: LIMITS.tzOffsetMinMax })), dev: s.optional(s.boolean()) }),
  'profile.save': s.object({
    gameState: s.json({ maxBytes: LIMITS.maxGameStateBytes, maxDepth: LIMITS.maxGameStateDepth }),
    baseRevision: s.int({ min: 0 }),
    reason: s.optional(s.literal(...SAVE_REASONS)),
    summary: s.optional(summaryInputValidator),
    team: s.optional(teamSnapshotInputValidator),
    consumedGrants: s.optional(s.array(id, { max: LIMITS.maxConsumedGrants })),
  }),
  'profile.me': empty,
  'profile.setDisplayName': s.object({ displayName }),
  'profile.getPublic': s.object({ playerId: id }),
  'profile.getPublicBatch': s.object({ playerIds: s.array(id, { max: LIMITS.maxPublicBatch }) }),
  'profile.importLegacy': s.object({ legacy: s.string({ min: 1, max: 4000 }), baseRevision: s.int({ min: 0 }) }),
  'profile.listGrants': s.object({ limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['profile.listGrants'].max })), cursor: s.optional(cursorValidator) }),

  'player.command': s.object({
    op: s.string({ min: 1, max: 64, pattern: COMMAND_OP_PATTERN }),
    args: commandArgsValidator,
    opId: s.string({ pattern: REQUEST_ID_PATTERN }),
  }),

  'leaderboard.get': s.refine(
    s.object({
      board: s.literal(...LEADERBOARD_BOARDS),
      page: s.optional(s.int({ min: 1, max: 1000 })),
      pageSize: s.optional(s.int({ min: 1, max: LIMITS.leaderboardMaxPageSize })),
      limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['leaderboard.get'].max })),
      cursor: s.optional(cursorValidator),
    }),
    (v) => (v.cursor !== undefined && v.page !== undefined ? 'use either page or cursor' : v.limit !== undefined && v.pageSize !== undefined ? 'use either pageSize or limit' : null),
    { schema: { not: { anyOf: [{ required: ['page', 'cursor'] }, { required: ['pageSize', 'limit'] }] } } },
  ),

  'assault.getZones': empty,
  'assault.getTargets': s.object({ zone: s.optional(s.int({ min: 1, max: 64 })) }),
  'assault.report': s.object({
    zone: s.int({ min: 1, max: 64 }),
    outcome: s.literal('win', 'loss'),
    expectedStage: s.int({ min: 0, max: LIMITS.maxCounter }),
    sessionId: s.optional(id),
    team: s.optional(teamSnapshotInputValidator),
  }),
  'assault.claimTribute': empty,
  'clan.list': s.object({
    sort: s.optional(s.literal('rating', 'new')),
    query: s.optional(s.string({ min: CLAN_QUERY_MIN, max: CLAN_QUERY_MAX })),
    limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['clan.list'].max })),
    cursor: s.optional(cursorValidator),
  }),
  'clan.get': s.object({ clanId }),
  'clan.mine': empty,
  'clan.members': s.object({
    clanId,
    limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['clan.members'].max })),
    cursor: s.optional(cursorValidator),
  }),
  'clan.create': s.object({ name: clanName, emblem: s.int({ min: 0, max: 1000 }), joinPolicy: s.optional(s.literal(...CLAN_JOIN_POLICIES)), description: s.optional(clanDescription) }),
  'clan.update': s.object({
    clanId,
    name: s.optional(clanName),
    emblem: s.optional(s.int({ min: 0, max: 1000 })),
    joinPolicy: s.optional(s.literal(...CLAN_JOIN_POLICIES)),
    description: s.optional(clanDescription),
  }),
  'clan.join': s.object({ clanId }),
  'clan.request': s.object({ clanId }),
  'clan.cancelRequest': s.object({ clanId }),
  'clan.requests': s.object({ clanId, limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['clan.requests'].max })), cursor: s.optional(cursorValidator) }),
  'clan.decide': s.object({ clanId, playerId: id, accept: s.boolean() }),
  'clan.kick': s.object({ clanId, playerId: id }),
  'clan.setRole': s.object({ clanId, playerId: id, role: s.literal('officer', 'member') }),
  'clan.transfer': s.object({ clanId, playerId: id }),
  'clan.leave': empty,
  'clan.disband': s.object({ clanId, confirmName: clanName }),
  'clan.history': s.object({ clanId, limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['clan.history'].max })), cursor: s.optional(cursorValidator) }),

  'chat.channels': s.object({ exclude: s.optional(s.array(id, { max: 200 })) }),
  'chat.history': s.object({ channel: chatChannel, limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['chat.history'].max })), cursor: s.optional(cursorValidator) }),
  'chat.send': s.object({ channel: chatChannel, text: chatText }),
  'chat.subscribe': s.object({ channels: s.array(chatChannel, { min: 1, max: 4 }) }),
  'chat.unsubscribe': s.object({ channels: s.optional(s.array(chatChannel, { max: 4 })) }),
  'chat.markRead': s.object({ channel: chatChannel, seq: s.int({ min: 0, max: Number.MAX_SAFE_INTEGER }) }),

  'auction.get': empty,
  'store.catalog': empty,
  'ads.status': empty,
  'ads.start': s.object({
    placement: s.literal(...AD_PLACEMENT_LIST),
    provider: s.literal(...AD_PROVIDERS),
    context: s.optional(s.object({ battleId: s.optional(s.string({ min: 1, max: 64, pattern: /^[A-Za-z0-9_-]+$/ })), index: s.optional(s.int({ min: 1, max: 16 })) })),
  }),
  'ads.ticket': s.object({ ticketId: adTicketId }),
  'ads.testReward': s.object({ ticketId: adTicketId }),

  'news.list': s.object({ limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['news.list'].max })), cursor: s.optional(cursorValidator) }),

  'replay.save': s.object({
    mode: s.literal(...BATTLE_MODES),
    stage: s.int({ min: 0, max: 100_000 }),
    result: battleResult,
    init: s.optional(battleInitValidator),
    seed: s.optional(s.int({ min: 0, max: 399 })),
    log: s.optional(replayLogValidator),
    legacy: s.optional(s.string({ min: 1, max: LIMITS.maxLegacyReplayChars })),
    battleId: s.optional(id),
  }),
  'replay.list': s.object({
    playerId: s.optional(id),
    limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['replay.list'].max })),
    cursor: s.optional(cursorValidator),
  }),
  'replay.get': s.object({ replayId: id }),

  'matchmaking.join': s.refine(
    s.object({
      mode: s.literal('just'),
      teams: s.int({ min: 2, max: 4 }),
      wormsPerTeam: s.int({ min: 1, max: 8 }),
    }),
    (v) => (v.teams * v.wormsPerTeam > 12 ? 'at most 12 worms in total (new_interf.as:3346)' : null),
    { description: 'teams x wormsPerTeam at most 12' },
  ),
  'matchmaking.leave': s.object({ ticketId: id }),
  'room.list': s.object({ limit: s.optional(s.int({ min: 1, max: LIST_LIMITS['room.list'].max })), cursor: s.optional(cursorValidator) }),
  'room.get': s.object({ roomId: id }),
  'room.create': s.object({
    settings: s.optional(roomSettingsValidator),
    private: s.optional(s.boolean()),
    maxPlayers: s.optional(s.int({ min: 2, max: 4 })),
  }),
  'room.join': s.refine(
    s.object({ roomId: s.optional(id), code: s.optional(s.string({ min: 4, max: 12, pattern: /^[A-Z0-9]+$/ })) }),
    (v) => (v.roomId === undefined && v.code === undefined ? 'either roomId or code is required' : null),
    { schema: { anyOf: [{ required: ['roomId'] }, { required: ['code'] }] } },
  ),
  'room.leave': s.object({ roomId: id }),
  'room.update': s.object({ roomId: id, settings: roomSettingsValidator }),
  'room.setReady': s.object({ roomId: id, ready: s.boolean() }),
  'room.setTeam': s.object({ roomId: id, team: s.int({ min: 1, max: 4 }) }),
  'room.chat': s.object({ roomId: id, text: s.string({ min: 1, max: LIMITS.chatMax }) }),
  'room.start': s.object({ roomId: id }),

  'session.start': s.object({ init: battleInitValidator }),
  'session.get': s.object({ sessionId: id }),
  'session.input': s.object({
    sessionId: id,
    turn: s.int({ min: 0 }),
    fromTick: s.int({ min: 0 }),
    throughTick: s.int({ min: -1 }),
    commands: s.array(inputCommandValidator, { max: LIMITS.maxInputBatch }),
  }),
  'session.endTurn': s.object({
    sessionId: id,
    turn: s.int({ min: 0 }),
    tick: s.int({ min: 0 }),
    nextTeam: s.int({ min: 1, max: 64 }),
    forcedEnd: s.optional(s.int({ min: -1, max: MAX_TICK })),
  }),
  'session.hash': s.object({ sessionId: id, tick: s.int({ min: 0 }), hash, algorithm: s.optional(s.string({ min: 1, max: 32 })) }),
  'session.finish': s.object({ sessionId: id, result: battleResult, finalHash: s.optional(hash), log: s.optional(commandLogValidator) }),
  'session.forfeit': s.object({ sessionId: id }),
  'session.dispute': s.object({ sessionId: id, tick: s.int({ min: 0, max: MAX_TICK }), reason: s.optional(s.string({ max: 200 })) }),
};

export type PayloadValidation<T extends RequestType> = { ok: true; value: PayloadOf<T> } | { ok: false; issues: Issue[]; message: string };

export function validatePayload<T extends RequestType>(type: T, payload: unknown): PayloadValidation<T> {
  const r = validate(PAYLOAD_VALIDATORS[type] as Validator<PayloadOf<T>>, payload);
  return r.ok ? r : { ok: false, issues: r.issues, message: formatIssues(r.issues) };
}
